/**
 * Server state for Team → At risk and Clients → Targets (build step 6). Every read and write is a contract
 * route (packages/contracts routes.step6): GET /at-risk; GET /targets, POST /clients/{id}/targets,
 * PATCH /clients/{id}/targets/{tid} (If-Match), POST /targets/import.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { ClientTarget, CreateTargetBody, ImportTargetsBody, ListTargetsQuery, RiskKind, UpdateTargetBody } from '@amplispot/contracts';
import { ApiFailure, call, newIdempotencyKey } from '../api';

export const gkeys = {
  atRisk: (kind: RiskKind | 'all') => ['atrisk', kind] as const,
  targets: (q: ListTargetsQuery) => ['targets', q] as const,
};

/** A target or risk change moves the hub badges (GET /me menu) too. */
export function refreshTargets(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: ['targets'] });
  void qc.invalidateQueries({ queryKey: ['me'] });
}

/** GET /at-risk, worst first, a page at a time ("Show more"); `summary` is over the whole ranking. */
export function useAtRisk(kind: RiskKind | 'all') {
  return useInfiniteQuery({
    queryKey: gkeys.atRisk(kind),
    queryFn: ({ pageParam }) => call('atRisk.list', { query: { limit: 50, cursor: pageParam, kind: kind === 'all' ? undefined : kind } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p) => p.next_cursor ?? undefined,
  });
}

/** GET /targets for a quarter (the server defaults to the current one), optionally one SPOC. */
export function useTargets(q: ListTargetsQuery) {
  return useQuery({ queryKey: gkeys.targets(q), queryFn: () => call('targets.list', { query: q }) });
}

export function useCreateTarget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { clientId: string; body: CreateTargetBody }) =>
      call('targets.create', { params: { id: v.clientId }, body: v.body, idempotencyKey: newIdempotencyKey() }),
    onSuccess: () => refreshTargets(qc),
  });
}

export function useUpdateTarget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { clientId: string; id: string; version: number; body: UpdateTargetBody }) =>
      call('targets.update', { params: { id: v.clientId, tid: v.id }, body: v.body, ifMatch: v.version }),
    onSuccess: () => refreshTargets(qc),
    // 412: someone else saved first. Refetch so reopening the row edits their version, not the stale one again.
    onError: (e) => {
      if (e instanceof ApiFailure && e.status === 412) refreshTargets(qc);
    },
  });
}

/** POST /targets/import. A dry run previews (nothing written); the real run applies every row or none. */
export function useImportTargets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ImportTargetsBody) => call('targets.import', { body, idempotencyKey: newIdempotencyKey() }),
    onSuccess: (r) => {
      if (!r.dry_run) refreshTargets(qc);
    },
  });
}

/** The prototype's `nf()`: Indian digit grouping (14,85,221), "-" when there is no figure. */
export const nf = (n: number | null | undefined) => (n == null ? '-' : Math.round(n).toLocaleString('en-IN'));

/** A pasted line the parser could not read: 1-based line number, the text, and why (shown in the modal). */
export type SheetProblem = { line: number; text: string; why: string };
export type SheetParse = { rows: ImportTargetsBody['rows']; problems: SheetProblem[] };

const COLS = ['User base', 'So far', 'Target'] as const;

/**
 * One line's cells. One separator per line, so a grouped number (2,52,932 — how the app prints counts) stays one
 * cell: tabs when the line has any (a copy from a sheet); else ", " (comma and space); else two or more spaces;
 * else a plain comma, with "quoted, cells" kept whole (a CSV export). Empty cells are kept, so columns never shift.
 */
function splitLine(l: string): string[] {
  if (l.includes('\t')) return l.split('\t');
  if (/,\s/.test(l)) return l.split(/\s*,\s+/);
  if (/\S\s{2,}\S/.test(l)) return l.split(/\s{2,}/);
  const out: string[] = [];
  let cur = '';
  let q = false;
  for (const ch of l) {
    if (ch === '"') q = !q;
    else if (ch === ',' && !q) {
      out.push(cur);
      cur = '';
    } else cur += ch;
  }
  out.push(cur);
  return out;
}

/** A count cell: grouping commas, spaces and a ₹ sign are ignored. '' → null (an empty cell); NaN → not a number. */
function cellNumber(c: string): number | null {
  const t = c.replace(/[,\s₹]/g, '');
  if (!t) return null;
  return /^\d+(\.\d+)?$/.test(t) ? Number(t) : NaN;
}

/**
 * The prototype's paste parser (`tgParse`): each line is `Client, User base, So far, Target`, or
 * `Client, So far, Target`, or `Client, So far`. A first line with no numbers is a header and is skipped. A Platform
 * column after the client (`Client, Platform, …`, to pick one of a client's several targets) is read only when that
 * header names it: without one, a text second cell ("N/A", "TBD", "1.2k" in User base) is a cell that is not a
 * number, never quietly re-read as a platform (which would add a new target instead of updating the real one).
 * A line that cannot be read is reported in `problems` — never dropped or read into the wrong columns.
 */
export function parseSheetRows(text: string): SheetParse {
  const rows: ImportTargetsBody['rows'] = [];
  const problems: SheetProblem[] = [];
  let seenData = false;
  // Set by a header row whose second column is Platform (or Label): every line then has that column.
  let labelCol = false;
  text.split('\n').forEach((raw, idx) => {
    const l = raw.replace(/\r$/, '').replace(/^ +| +$/g, '');
    if (!l.trim()) return;
    const bad = (why: string) => problems.push({ line: idx + 1, text: l.trim(), why });
    let cells = splitLine(l).map((x) => x.trim());
    // A line copied with trailing tabs (empty columns past the last) is not a column shift.
    while (cells.length > 2 && cells[cells.length - 1] === '') cells = cells.slice(0, -1);
    const name = cells[0] ?? '';
    if (!seenData && !cells.slice(1).map(cellNumber).some((v) => v !== null && !isNaN(v))) {
      seenData = true;
      if (/^client\b/i.test(name)) {
        labelCol = /platform|label/i.test(cells[1] ?? '');
        return; // the header row
      }
    }
    seenData = true;
    const label = labelCol ? cells[1] ?? '' : '';
    const first = labelCol ? 2 : 1;
    const vals = cells.slice(first).map(cellNumber);
    if (!name) return bad('The client name is empty.');
    // POST /targets/import caps a label at 2-80 characters; a cut-down one would never match and would add a new target.
    if (label && (label.length < 2 || label.length > 80)) return bad(`The Platform "${label.length > 40 ? `${label.slice(0, 40)}…` : label}" must be 2 to 80 characters.`);
    if (vals.length < 1) return bad('No numbers after the client name. Use Client, So far (or Client, User base, So far, Target).');
    if (vals.length > 3) return bad(`${vals.length} numbers after the client name; at most 3 (User base, So far, Target).`);
    // 3 numbers: User base, So far, Target; 2: So far, Target; 1: So far.
    const cols: readonly (typeof COLS)[number][] = vals.length === 3 ? COLS : vals.length === 2 ? ['So far', 'Target'] : ['So far'];
    const at = (c: (typeof COLS)[number]) => vals[cols.indexOf(c)];
    const nan = cols.find((c) => Number.isNaN(at(c)));
    if (nan) {
      const i = first + cols.indexOf(nan);
      // A text cell straight after the client may be a Platform: say how to have it read as one.
      const hint = i === 1 && /[a-z]{2}/i.test(cells[i] ?? '') ? ' For a Platform column, start with a header row: Client, Platform, User base, So far, Target.' : '';
      return bad(`"${cells[i]}" in ${nan} is not a number.${hint}`);
    }
    const current = at('So far');
    if (current == null) return bad('So far is empty.');
    const base = cols.includes('User base') ? at('User base') : undefined;
    const target = cols.includes('Target') ? at('Target') : undefined;
    rows.push({
      client: name,
      ...(label ? { label } : {}),
      ...(base != null ? { base_value: base } : {}),
      current_value: current,
      ...(target != null && target > 0 ? { target_value: target } : {}),
    });
  });
  return { rows, problems };
}

const SOURCE_WORDS: Record<ClientTarget['source'], string> = {
  sheet: 'Google Sheet',
  import: 'pasted from the tracker sheet',
  manual: 'typed in here',
};

/**
 * The Targets "Source:" note, from the rows' real `source`: one phrase per source present, in a fixed order
 * ("Google Sheet, pasted from the tracker sheet and typed in here"). `fromSheet` says whether any row came from a
 * sheet (sync or paste), so the "not written back to the sheet" warning shows only then.
 */
export function targetSourceWords(rows: readonly Pick<ClientTarget, 'source'>[]): { words: string; fromSheet: boolean } {
  const present = (['sheet', 'import', 'manual'] as const).filter((k) => rows.some((r) => r.source === k));
  const parts = present.map((k) => SOURCE_WORDS[k]);
  const words = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : (parts[0] ?? '');
  return { words, fromSheet: present.some((k) => k !== 'manual') };
}
